import type { LoggableError } from 'typings'

const REQUEST_ERROR = Symbol.for('promotions.loggedRequestError')

export function isResponseLike(
  value: unknown
): value is { status: number; statusText?: string; url?: string } {
  if (typeof value !== 'object' || value === null) return false

  const candidate = value as { status?: unknown; text?: unknown }

  return (
    typeof candidate.status === 'number' && typeof candidate.text === 'function'
  )
}

export function markLoggedRequestError<T>(error: T): T {
  if (
    error !== null &&
    (typeof error === 'object' || typeof error === 'function')
  ) {
    try {
      Object.defineProperty(error, REQUEST_ERROR, {
        value: true,
        enumerable: false,
      })
    } catch {
      // Frozen or sealed values cannot be tagged. Callers still log them once.
    }
  }

  return error
}

export function hasLoggedRequestError(
  error: unknown,
  seen = new Set<unknown>()
): boolean {
  if (error === null || typeof error !== 'object') return false
  if (seen.has(error)) return false

  seen.add(error)

  if ((error as { [REQUEST_ERROR]?: boolean })[REQUEST_ERROR] === true) {
    return true
  }

  const cause = (error as { cause?: unknown }).cause

  if (hasLoggedRequestError(cause, seen)) return true

  if (cause !== null && typeof cause === 'object' && 'error' in cause) {
    return hasLoggedRequestError((cause as { error?: unknown }).error, seen)
  }

  return false
}

/** Plain JSON value. Error and Response become { name, message, stack, status, url }. */
export function toLoggableError(error: unknown): LoggableError {
  if (isResponseLike(error)) {
    const statusText = error.statusText ?? ''

    return {
      name: 'Response',
      message: statusText || `Request failed with status ${error.status}`,
      status: error.status,
      statusText,
      url: error.url ?? '',
    }
  }

  if (error instanceof Error) {
    const extra = error as Error & {
      status?: unknown
      statusText?: unknown
      url?: unknown
      code?: unknown
    }
    const loggable: LoggableError = {
      name: error.name,
      message: error.message,
    }

    if (typeof error.stack === 'string') loggable.stack = error.stack
    if (typeof extra.status === 'number') loggable.status = extra.status
    if (typeof extra.statusText === 'string')
      loggable.statusText = extra.statusText
    if (typeof extra.url === 'string') loggable.url = extra.url
    if (typeof extra.code === 'string') loggable.code = extra.code

    return loggable
  }

  if (typeof error === 'string') return { message: error }

  if (error !== null && typeof error === 'object' && 'message' in error) {
    const record = error as {
      name?: unknown
      message?: unknown
      stack?: unknown
      status?: unknown
      statusText?: unknown
      url?: unknown
      code?: unknown
    }
    const loggable: LoggableError = {
      message:
        typeof record.message === 'string' ? record.message : String(error),
    }

    if (typeof record.name === 'string') loggable.name = record.name
    if (typeof record.stack === 'string') loggable.stack = record.stack
    if (typeof record.status === 'number') loggable.status = record.status
    if (typeof record.statusText === 'string')
      loggable.statusText = record.statusText
    if (typeof record.url === 'string') loggable.url = record.url
    if (typeof record.code === 'string') loggable.code = record.code

    return loggable
  }

  return { message: String(error) }
}

export function toJsonValue(
  value: unknown,
  seen = new WeakSet<object>()
): unknown {
  if (value === undefined) return undefined
  if (value === null) return null
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value
  }
  if (typeof value === 'bigint') return value.toString()
  if (typeof value === 'function' || typeof value === 'symbol') return undefined
  if (isResponseLike(value) || value instanceof Error)
    return toLoggableError(value)
  if (typeof value !== 'object') return String(value)
  if (seen.has(value)) return '[Circular]'

  seen.add(value)

  if (Array.isArray(value)) {
    return value.map((item) => {
      const json = toJsonValue(item, seen)

      return json === undefined ? null : json
    })
  }

  const output: Record<string, unknown> = {}
  const record = value as Record<string, unknown>

  for (const [key, nested] of Object.entries(record)) {
    const json = toJsonValue(nested, seen)

    if (json !== undefined) output[key] = json
  }

  return output
}
